/**
 * TextField layout (render-ready lines and runs). Rules follow Ruffle's html/layout.rs:
 * - paragraphs split at '\r'; greedy word wrap at spaces when wordWrap (overlong words break by char);
 * - line height = max ascent + max descent; line advance adds the leading of the line's first span;
 * - the very first line's leading counts towards textHeight; for non-input fields an empty last
 *   line does not count towards the text size;
 * - alignment ignores trailing spaces; offsets never negative; tabs advance to multiples of
 *   2.7 * font size (or explicit tab stops).
 * Measurement comes from the Platform (headless: deterministic width table), so exact numLines /
 * textWidth parity with Flash depends on the host's fonts (UNVERIFIED for headless).
 */
import { Fmt, FormatSpans } from './spans';
import { fontMetrics, measureText } from './metrics';
import type { FontSpec } from '../platform/types';

export interface LayoutRun {
  /** Text to draw (password fields: asterisks). */
  text: string;
  /** Character range [start, end) in the field text. */
  start: number;
  end: number;
  /** x of the run start relative to the text area origin (after the 2px gutter). */
  x: number;
  width: number;
  fmt: Fmt;
  /** The text is sensitive (a password field): measure it without the platform's cache (caret, selection). */
  noCache?: boolean;
}

export interface LayoutLine {
  index: number;
  /** Character range [start, end) (end excludes the paragraph newline). */
  start: number;
  end: number;
  /** Line box left x and width (without trailing spaces for non-left alignment). */
  x: number;
  width: number;
  /** Top of the line box (y), baseline = y + ascent. */
  y: number;
  ascent: number;
  descent: number;
  leading: number;
  runs: LayoutRun[];
}

export interface TextLayout {
  lines: LayoutLine[];
  textWidth: number;
  textHeight: number;
}

export interface LayoutOptions {
  /** Available content width (bounds width - 4), or null for unconstrained (autosize w/o wrap). */
  width: number | null;
  wordWrap: boolean;
  password: boolean;
  isInput: boolean;
  /**
   * A password field, also while its text is shown (TextField.$sensitive): its text is measured without the
   * platform's measurement cache, so no password (or prefix of it) stays in page memory.
   */
  sensitive?: boolean;
}

function spec(f: Fmt): FontSpec {
  return { family: f.font, size: f.size, bold: f.bold, italic: f.italic };
}

function measure(text: string, f: Fmt, noCache = false): number {
  if (!text.length) return 0;
  return measureText(text, spec(f), noCache) + f.letterSpacing * text.length;
}

interface Piece {
  start: number;
  end: number;
  fmt: Fmt;
  isSpace: boolean;
  isTab: boolean;
}

/** Round to the twip grid (Flash layout works in twips). */
function tw(v: number): number {
  return Math.round(v * 20) / 20;
}


/** Marks, variation selectors and the zero-width joiner: they belong to the character before them. */
const CLUSTER_TAIL = /[\p{M}\u200d\ufe00-\ufe0f]/u;

/**
 * The end of the character that starts at `i` (before `end`): a UTF-16 pair is one character, and the marks,
 * variation selectors and zero-width joiners after it (with the character a joiner joins) go with it.
 */
export function clusterEnd(text: string, i: number, end: number): number {
  const step = (k: number) => {
    const c = text.charCodeAt(k);
    return c >= 0xd800 && c <= 0xdbff && k + 1 < end && (text.charCodeAt(k + 1) & 0xfc00) === 0xdc00 ? k + 2 : k + 1;
  };
  let e = step(i);
  while (e < end) {
    const cp = text.codePointAt(e)!;
    const ch = String.fromCodePoint(cp);
    if (!CLUSTER_TAIL.test(ch)) break;
    e = step(e);
    if (ch === '\u200d' && e < end) e = step(e);
  }
  return Math.min(e, end);
}

export function layoutText(fs: FormatSpans, defaultFmt: Fmt, opts: LayoutOptions): TextLayout {
  const text = fs.text;
  // The masked text of a password field is only asterisks: the cache may keep it. Clear text of a sensitive field not.
  const noCache = !!opts.sensitive && !opts.password;
  const display = opts.password ? text.replace(/[^\r]/g, '*') : text;
  const lines: LayoutLine[] = [];
  const spanList: Array<[number, number, Fmt]> = Array.from(fs.iter());
  const fmtAtIndex = (i: number): Fmt => {
    for (const [s, e, f] of spanList) if (i >= s && i < e) return f;
    return spanList.length ? spanList[spanList.length - 1][2] : defaultFmt;
  };

  let y = 0;
  let lineIndex = 0;
  let paraStart = 0;
  const avail = opts.width;

  const pushLine = (start: number, end: number, pieces: Piece[], firstOfPara: boolean, paraFmt: Fmt) => {
    // Metrics and runs.
    let ascent = 0;
    let descent = 0;
    const lineFmt = pieces.length ? pieces[0].fmt : paraFmt;
    const leading = lineFmt.leading;
    const runs: LayoutRun[] = [];
    let x = 0;
    if (pieces.length === 0) {
      const m = fontMetrics(spec(paraFmt));
      ascent = m.ascent;
      descent = m.descent;
    }
    for (const p of pieces) {
      const m = fontMetrics(spec(p.fmt));
      if (m.ascent > ascent) ascent = m.ascent;
      if (m.descent > descent) descent = m.descent;
      let w: number;
      if (p.isTab) {
        const stops = p.fmt.tabStops;
        let nx = x;
        if (!stops.length) {
          const mod = p.fmt.size * 2.7;
          nx = (Math.floor(x / mod) + 1) * mod;
        } else {
          for (const s of stops) {
            if (s > x) {
              nx = s;
              break;
            }
          }
        }
        w = nx - x;
      } else w = measure(display.slice(p.start, p.end), p.fmt, noCache);
      const last = runs[runs.length - 1];
      if (last && last.fmt === p.fmt && last.end === p.start && !p.isTab) {
        last.end = p.end;
        last.text = display.slice(last.start, last.end);
        last.width = measure(last.text, p.fmt, noCache);
      } else {
        const run: LayoutRun = { text: p.isTab ? '' : display.slice(p.start, p.end), start: p.start, end: p.end, x, width: w, fmt: p.fmt };
        if (noCache) run.noCache = true;
        runs.push(run);
      }
      x = runs[runs.length - 1].x + runs[runs.length - 1].width;
    }
    let width = x;
    const align = lineFmt.bullet ? 'left' : lineFmt.align;
    if (align !== 'left' && runs.length) {
      // Trailing spaces are ignored when aligning.
      let trimmed = x;
      for (let i = pieces.length - 1; i >= 0 && pieces[i].isSpace; i--) {
        const r = runs.find((rr) => rr.start <= pieces[i].start && rr.end >= pieces[i].end);
        if (!r) break;
        trimmed -= measure(display.slice(pieces[i].start, pieces[i].end), pieces[i].fmt, noCache);
      }
      width = Math.max(0, trimmed);
    }
    const leftAdj = lineFmt.leftMargin + lineFmt.blockIndent + (firstOfPara ? lineFmt.indent : 0);
    const rightAdj = lineFmt.rightMargin;
    let alignAdj = 0;
    if (avail !== null) {
      const mis = avail - leftAdj - rightAdj - width;
      if (align === 'center') alignAdj = Math.max(0, mis / 2);
      else if (align === 'right') alignAdj = Math.max(0, mis);
    }
    const ox = leftAdj + alignAdj;
    for (const r of runs) r.x = tw(r.x + ox);
    lines.push({ index: lineIndex++, start, end, x: tw(ox), width: tw(width), y: tw(y), ascent, descent, leading, runs });
    y += ascent + descent + leading;
  };

  while (paraStart <= text.length) {
    let paraEnd = text.indexOf('\r', paraStart);
    const hasNl = paraEnd >= 0;
    if (!hasNl) paraEnd = text.length;
    const paraFmt = fmtAtIndex(paraStart < text.length ? paraStart : Math.max(0, paraStart - 1));
    // Tokenize paragraph into pieces: words, spaces, tabs (split at span boundaries too).
    const pieces: Piece[] = [];
    for (const [s, e, f] of spanList) {
      const a = Math.max(s, paraStart);
      const b = Math.min(e, paraEnd);
      if (a >= b) continue;
      let i = a;
      while (i < b) {
        const c = text[i];
        if (c === '\t') {
          pieces.push({ start: i, end: i + 1, fmt: f, isSpace: true, isTab: true });
          i++;
          continue;
        }
        const sp = c === ' ';
        let j = i + 1;
        while (j < b && (text[j] === ' ') === sp && text[j] !== '\t') j++;
        pieces.push({ start: i, end: j, fmt: f, isSpace: sp, isTab: false });
        i = j;
      }
    }
    // Greedy line filling.
    let lineStart = paraStart;
    let cur: Piece[] = [];
    let curW = 0;
    let firstOfPara = true;
    const maxW = (first: boolean) => {
      if (avail === null) return Infinity;
      const lf = cur.length ? cur[0].fmt : paraFmt;
      return avail - lf.leftMargin - lf.rightMargin - lf.blockIndent - (first ? lf.indent : 0);
    };
    const pieceWidth = (p: Piece, atX: number) => {
      if (p.isTab) {
        const mod = p.fmt.size * 2.7;
        return (Math.floor(atX / mod) + 1) * mod - atX;
      }
      return measure(display.slice(p.start, p.end), p.fmt, noCache);
    };
    for (let k = 0; k < pieces.length; k++) {
      const p = pieces[k];
      const w = pieceWidth(p, curW);
      if (opts.wordWrap && !p.isSpace && cur.length && curW + w > maxW(firstOfPara)) {
        // Move the word containing `p` (it may span several format pieces) to the next line.
        let n = cur.length;
        let nextStart = p.start;
        while (n > 0 && !cur[n - 1].isSpace && cur[n - 1].end === nextStart) {
          nextStart = cur[n - 1].start;
          n--;
        }
        if (n > 0) {
          const moved = cur.slice(n);
          const breakAt = moved.length ? moved[0].start : p.start;
          pushLine(lineStart, breakAt, cur.slice(0, n), firstOfPara, paraFmt);
          firstOfPara = false;
          lineStart = breakAt;
          cur = moved;
          curW = 0;
          for (const m of cur) curW += pieceWidth(m, curW);
        }
      }
      if (opts.wordWrap && !p.isSpace && !cur.length && w > maxW(firstOfPara) && p.end - p.start > 1) {
        // Overlong word: break by characters (never inside a character of two UTF-16 units, nor before the marks and
        // joined parts that belong to it: half an emoji or an accent on its own draws as garbage).
        let s = p.start;
        while (s < p.end) {
          let e = clusterEnd(text, s, p.end);
          for (let n = clusterEnd(text, e, p.end); e < p.end && measure(display.slice(s, n), p.fmt, noCache) <= maxW(firstOfPara); n = clusterEnd(text, n, p.end)) e = n;
          const piece: Piece = { start: s, end: e, fmt: p.fmt, isSpace: false, isTab: false };
          if (e < p.end) {
            pushLine(lineStart, e, [piece], firstOfPara, paraFmt);
            firstOfPara = false;
            lineStart = e;
          } else {
            cur.push(piece);
            curW = measure(display.slice(s, e), p.fmt, noCache);
          }
          s = e;
        }
        continue;
      }
      cur.push(p);
      curW += pieceWidth(p, curW);
    }
    pushLine(lineStart, paraEnd, cur, firstOfPara, paraFmt);
    if (!hasNl) break;
    paraStart = paraEnd + 1;
  }

  // Text size: union of line boxes; first line includes its leading; empty last line of a
  // non-input field is ignored.
  let minX = Infinity;
  let maxX = -Infinity;
  let bottom = 0;
  let any = false;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    const isLast = i === lines.length - 1;
    const empty = l.start === l.end;
    if (!opts.isInput && empty && isLast) continue;
    any = true;
    minX = Math.min(minX, l.x);
    maxX = Math.max(maxX, l.x + l.width);
    let b = l.y + l.ascent + l.descent;
    if (i === 0) b += l.leading;
    bottom = Math.max(bottom, b);
  }
  return { lines, textWidth: any ? tw(maxX - minX) : 0, textHeight: any ? tw(bottom) : 0 };
}
